import c from 'classnames';
import Carousel from 'core/components/carousel';
import { NextButton, PreviousButton } from 'core/components/carousel/controls';
import { DEFAULT_SOON_CAUSE } from 'core/const';
import { ieFix } from 'core/style';
import injectSheet, { createStyles } from 'core/style/injectSheet';
import { ITheme, WithStyles } from 'core/style/interfaces';
import { formatToLocalDayMonth, formatToLocalDayMonthYearDot, isImpossibleDate, isMobile } from 'core/utils';
import {
    getFormFirstDate,
    getFormSoonCause,
    IGetFormFirstDateProductForm,
    IGetFormSoonCauseProductForm,
} from 'core/utils/product-forms/productForms';
import React from 'react';
import { ProductForm, ProductFormPublicTypeEnum } from 'site/graphql';

export type IProductTrainingOptionsProductForm = IGetFormSoonCauseProductForm &
    IGetFormFirstDateProductForm &
    Pick<ProductForm, 'id' | 'title' | 'price'>;

type IProductTrainingOptionsProps = {
    availableProductForms: IProductTrainingOptionsProductForm[];
    selectedProductFormIndex: number;
    onProductFormSelect: (index: number) => void;
} & WithStyles<typeof styles>;

interface MyState {
    activeItemIndex: number;
    activeSlideIndex: number;
    darkenWhenBoundaryPrevBtn: boolean;
    darkenWhenBoundaryNextBtn: boolean;
}

class ProductTrainingOptions extends React.Component<IProductTrainingOptionsProps, MyState> {
    constructor(props) {
        super(props);

        this.state = {
            activeItemIndex: 0,
            activeSlideIndex: 0,
            darkenWhenBoundaryPrevBtn: true,
            darkenWhenBoundaryNextBtn: true,
        };
    }

    getStartDate(productForm: IProductTrainingOptionsProductForm): string | null {
        const startDate = getFormFirstDate(productForm);

        if (startDate === null) {
            return null;
        }

        // Специальная логика для записей
        if (productForm.type === ProductFormPublicTypeEnum.EventRecord) {
            if (isImpossibleDate(startDate)) {
                return null;
            } else {
                return `От ${formatToLocalDayMonthYearDot(startDate)}`;
            }
        }

        if (isImpossibleDate(startDate)) {
            const soonCause = getFormSoonCause(productForm);
            return soonCause || DEFAULT_SOON_CAUSE;
        } else {
            return formatToLocalDayMonth(startDate);
        }
    }

    render() {
        const { classes, selectedProductFormIndex, availableProductForms, onProductFormSelect } = this.props;
        const slidesToShow = isMobile(600) ? 1 : 2;
        const customControlStyles = {
            fill: 'black',
            opacity: undefined,
            display: availableProductForms.length > slidesToShow ? 'block' : 'none',
        };

        const productForm = availableProductForms.reduce((previousValue, item, index) => {
            const chunkIndex = Math.floor(index / 4);
            if (!previousValue[chunkIndex]) {
                previousValue[chunkIndex] = [];
            }

            previousValue[chunkIndex].push(item);

            return previousValue;
        }, []);

        const setItem = (pf, activeItemIndex) => {
            const realPfIndex = getRealPfIndex(pf);

            const newSlideIndex = productForm.findIndex(
                (group: []) => group.filter((item: any) => item.id === pf.id).length > 0,
            );
            this.setState({ activeSlideIndex: newSlideIndex, activeItemIndex: activeItemIndex });
            onProductFormSelect(realPfIndex);
        };

        const getRealPfIndex = pf => {
            return availableProductForms.findIndex(item => item.id === pf.id);
        };

        return (
            <>
                <div className={classes.title}>{`Выберите вариант обучения и нажмите ${
                    availableProductForms[selectedProductFormIndex]?.price === 0 ? 'присоединиться ' : 'записаться'
                }`}</div>

                <Carousel
                    swiping
                    slidesToShow={slidesToShow}
                    speed={250}
                    slideIndex={this.state.activeSlideIndex}
                    // Необходимо для того, чтобы когда Carousel вычисляет высоту максимального итема, принимала offsetWidth за 150, а не за 0
                    // (иначе текст сжимается и высота расчитывается неверно)
                    initialSlideWidth={150}
                    dragging={false}
                    renderBottomCenterControls={null}
                    classNameLi={classes.carouselLi}
                    renderCenterLeftControls={props => (
                        <PreviousButton {...props} customStyles={customControlStyles} darkenWhenBoundary />
                    )}
                    renderCenterRightControls={props => (
                        <NextButton {...props} customStyles={customControlStyles} darkenWhenBoundary />
                    )}
                    decoratorStyleCenterLeftControls={{ left: -25 }}
                    decoratorStyleCenterRightControls={{ right: -25 }}
                >
                    {isMobile(600)
                        ? productForm.map((group, index) => {
                              const isSlideSelected = this.state.activeSlideIndex === index;
                              return (
                                  <div className={classes.container} key={`slide-${index}`}>
                                      {group.map((pf, i) => {
                                          const startDate = this.getStartDate(pf);

                                          return (
                                              <div
                                                  key={pf.id}
                                                  className={c(classes.item, {
                                                      [classes.itemSelected]:
                                                          isSlideSelected && i === this.state.activeItemIndex,
                                                  })}
                                                  onClick={() => setItem(pf, i)}
                                              >
                                                  <div className={classes.itemTitle}>{pf.title}</div>
                                                  {startDate && <div>{startDate}</div>}
                                              </div>
                                          );
                                      })}
                                  </div>
                              );
                          })
                        : availableProductForms.map((pf, i) => {
                              const startDate = this.getStartDate(pf);
                              return (
                                  <div
                                      key={pf.id}
                                      className={c(classes.item, {
                                          [classes.itemSelected]: i === selectedProductFormIndex,
                                      })}
                                      onClick={() => onProductFormSelect(i)}
                                  >
                                      <div className={classes.itemTitle}>{pf.title}</div>
                                      {startDate && <div>{startDate}</div>}
                                  </div>
                              );
                          })}
                </Carousel>
            </>
        );
    }
}

const styles = (theme: ITheme) =>
    createStyles({
        title: {
            fontWeight: 'bold',
            marginBottom: 2 + 'rem',
            borderBottom: `4px solid ${theme.colors.accent}`,
            textAlign: 'center',
        },
        carouselLi: {
            display: 'flex !important',
            justifyContent: 'center',
        },
        item: {
            width: 150,
            height: 146,
            position: 'relative',
            border: `2px solid ${theme.colors.lightGray}`,
            padding: 10,
            cursor: 'pointer',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'start',
            overflow: 'hidden',
            ...ieFix({ height: 150 }),

            [theme.breakpoints.down('xs')]: {
                width: 48 + '%',

                '&:nth-of-type(2n)': { margin: '0 0 8px 9px' },
                '&:nth-of-type(4)': { marginBottom: 0 },
            },

            '&:after': {
                content: '""',
                position: 'absolute',
                bottom: -1,
                left: 0,
                width: '100%',
                height: 40,

                background: 'linear-gradient(to bottom, rgba(255, 255, 255, 0) 0%, #FFF 70%)',
                fallbacks: [
                    {
                        background:
                            '-moz-linear-gradient(top, rgba(255,255,255,0) 0%, rgba(255,255,255,1) 70%)' /* FF3.6-15 */,
                    },
                    {
                        background:
                            '-webkit-linear-gradient(top, rgba(255,255,255,0) 0%,rgba(255,255,255,1) 70%)' /* Chrome10-25,Safari5.1-6 */,
                    },
                ],
            },
        },
        itemSelected: {
            border: `2px solid ${theme.colors.accent}`,
        },
        itemTitle: {
            fontWeight: 'bold',
            wordWrap: 'break-word',
        },
        button: {
            marginTop: 2 + 'rem',
        },
        container: {
            width: 100 + '%',
            minHeight: 150,
            maxHeight: 300,
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'flex-start',
        },
    });

export default injectSheet(styles, 'ProductTrainingOptions')(ProductTrainingOptions);
